import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ShieldCheck, BookOpenCheck, Gift } from "lucide-react";
import { CHECKOUT_URL, PRICE_NEW, PRICE_OLD, scrollToId } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const bgTextY = useTransform(scrollYProgress, [0, 1], [0, -110]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="inicio"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden pb-24 pt-32 sm:pt-36 lg:pb-32 lg:pt-40"
    >
      <motion.span
        aria-hidden="true"
        style={{ y: bgTextY }}
        className="text-outline pointer-events-none absolute -right-10 top-16 select-none font-display text-[16rem] font-bold leading-none lg:text-[26rem]"
      >
        40+
      </motion.span>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="eyebrow mb-8 flex items-center gap-3 text-accent"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block h-px w-10 bg-accent" />
            Ebook · Emagrecer depois dos 40
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl font-semibold leading-[1.04] tracking-tight text-foreground sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.25}>O que funcionava</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="italic text-accent">parou de funcionar.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground"
          >
            O <strong className="font-semibold text-foreground">Plano 40+</strong> é o guia prático
            de 21 dias para recomeçar: emagreça com organização e constância, entenda o seu novo
            metabolismo e construa hábitos que duram — sem dietas restritivas.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <a
              data-testid="hero-primary-cta"
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-shine group inline-flex items-center gap-3 rounded-full bg-accent px-8 py-4 text-base font-semibold text-accent-foreground transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            >
              Quero o Plano 40+ por {PRICE_NEW}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("#capitulos")}
              className="inline-flex items-center gap-2 rounded-full border border-foreground/15 px-7 py-4 text-base font-medium text-foreground transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-primary hover:bg-secondary"
            >
              <BookOpenCheck className="h-5 w-5 text-primary" />
              Ver o que tem dentro
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted-foreground"
          >
            <span data-testid="hero-guarantee-badge" className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Garantia incondicional de 7 dias
            </span>
            <span className="inline-flex items-center gap-2">
              <Gift className="h-4 w-4 text-primary" />
              Bônus: guia dos erros comuns
            </span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div
            style={{ y: coverY }}
            className="relative mx-auto w-72 sm:w-80 lg:w-full lg:max-w-sm"
          >
            <div className="book-glow absolute -inset-16 rounded-full" aria-hidden="true" />
            <motion.div
              data-testid="hero-book-cover"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              initial={{ opacity: 0, y: 60, rotate: -3 }}
              animate={{ opacity: 1, y: 0, rotate: -3 }}
              transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
              className="relative overflow-hidden rounded-md shadow-[0_40px_80px_-20px_rgba(24,35,28,0.45)]"
            >
              <img
                src="/assets/plano40-capa.png"
                alt="Capa do ebook Plano 40+ — 21 Dias para Recomeçar"
                className="h-auto w-full"
                data-testid="hero-book-image"
              />
              <span
                className="pointer-events-none absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-black/25 to-transparent"
                aria-hidden="true"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
              data-testid="hero-price-badge"
              className="absolute -left-6 -top-6 rounded-2xl border border-border bg-card/90 px-5 py-3 shadow-lg backdrop-blur-md sm:-left-10"
              style={{ transform: "translateZ(40px)" }}
            >
              <p className="font-mono-accent text-xs text-muted-foreground line-through">{PRICE_OLD}</p>
              <p className="font-display text-3xl font-bold text-accent">{PRICE_NEW}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.35, ease: EASE }}
              className="absolute -bottom-8 -right-2 rounded-2xl border border-border bg-primary px-5 py-3 text-primary-foreground shadow-xl sm:-right-8"
            >
              <p className="font-mono-accent text-[0.65rem] uppercase tracking-[0.2em] opacity-80">
                Método de
              </p>
              <p className="font-display text-2xl font-semibold">21 dias</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
